/* apps/web/src/components/layout/navSlot.tsx
 *
 * The seam that lets the Copilot chat list render inside the app navigation.
 *
 * The design's default workspace layout ("C · Unified nav" in the canvas, and
 * the value its `layout` prop ships with) puts the chat list in the 288px app
 * rail rather than in a second column beside it. The conversation state,
 * though, belongs to the Copilot page — it owns the queries, the mutations and
 * the active-chat bookkeeping, and lifting all of that into the shell to
 * satisfy a visual arrangement would be the wrong trade.
 *
 * So the page keeps its state and portals the rendered list into a slot the
 * sidebar draws. Nothing about routing, data fetching or authorization moves.
 */
import { useLayoutEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

export const NAV_CHAT_SLOT_ID = "nav-chat-slot";

/**
 * Renders `children` into the sidebar's chat slot when it exists, and in place
 * when it does not.
 *
 * The slot is absent while the rail is collapsed, and altogether absent when
 * the page is rendered outside the app shell. The fallback matters: without it
 * the chat list would simply vanish in those cases, which is a worse failure
 * than showing it in the page — and it keeps the Copilot page renderable on
 * its own.
 */
export function NavChatSlot({ children }: { children: ReactNode }): JSX.Element {
  const [host, setHost] = useState<HTMLElement | null>(null);

  // Layout, not passive: the move into the rail happens before paint, so the
  // list is never seen in the page first.
  useLayoutEffect(() => {
    // The sidebar and the page are siblings, so a ref cannot join them. More
    // importantly, MainLayout can replace the slot when a viewport resize
    // crosses the collapsed-rail breakpoint without re-rendering the routed
    // page. That is exactly what opening and closing browser DevTools does.
    // Observe the shell rather than relying on this component rendering again,
    // otherwise the portal either stays attached to a detached element or the
    // chat list falls back into the conversation column.
    const findHost = (): void => {
      const found = document.getElementById(NAV_CHAT_SLOT_ID);
      setHost((current) => (current === found ? current : found));
    };

    findHost();
    const observer = new MutationObserver(findHost);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  if (host) return createPortal(children, host);

  // Inside MainLayout, an absent slot is intentional: the rail is collapsed
  // (or is being replaced during a responsive transition). Rendering in place
  // would put the entire chat history above the conversation. Keep the fallback
  // only for genuinely standalone renders of CopilotPage.
  return document.querySelector(".app-shell") ? <></> : <>{children}</>;
}
